import type { ReactNode } from 'react';
import React, { Component } from 'react';

import { reportJobException } from '@/features/custodial/services/jobErrorReporter';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        // a render crash mid-deposit/withdraw is invisible to the backend, and leaves the job
        // looking frozen. No-op unless an operation is actually in flight.
        reportJobException(error, {
            where: 'ErrorBoundary',
            detail: { componentStack: errorInfo?.componentStack?.slice(0, 1000) ?? null },
        });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen p-8">
                    <h1 className="text-4xl font-bold mb-4 text-red-600">
                        Something went wrong
                    </h1>
                    <p className="text-lg text-muted-foreground mb-4">
                        An unexpected error occurred. Please try again.
                    </p>
                    {this.state.error && (
                        <details className="mb-6 max-w-2xl w-full">
                            <summary className="cursor-pointer text-sm text-muted-foreground mb-2">
                                Error details
                            </summary>
                            <pre className="bg-gray-100 text-gray-900 p-4 rounded-lg overflow-auto text-sm">
                                {this.state.error.toString()}
                                {this.state.error.stack && (
                                    <div className="mt-2 text-xs">
                                        {this.state.error.stack}
                                    </div>
                                )}
                            </pre>
                        </details>
                    )}
                    <button
                        onClick={this.handleReset}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
